import { useState } from "react";
import { DownloadOutlined } from "@ant-design/icons";
import { Button, Space, Table, Tag } from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";

import sdk from "@/sdk";
import { CorrectionType } from "@/graphql";
import { formatBalance } from "@/utils/formatters";

import { DateRangeModal } from "../../widgets/DateRangeModal";

const CORRECTION_TYPE_LABELS: Record<string, string> = {
  [CorrectionType.CashWithdrawal]: "Бэлэн мөнгө",
  [CorrectionType.HoldWithdrawal]: "Худалдан авалтаас үлдсэн дүн",
  [CorrectionType.BondInterestRecalc]: "Бондын хүү",
  [CorrectionType.StockAdjustment]: "Хувьцаа",
  STOCK_AMOUNT_ADJUSTMENT: "Хувьцаа тоо ширхэг",
  STOCK_PRICE_ADJUSTMENT: "Хувьцаа ханш",
};

const CORRECTION_TYPE_COLORS: Record<string, string> = {
  [CorrectionType.CashWithdrawal]: "blue",
  [CorrectionType.HoldWithdrawal]: "orange",
  [CorrectionType.BondInterestRecalc]: "green",
  [CorrectionType.StockAdjustment]: "purple",
  STOCK_AMOUNT_ADJUSTMENT: "purple",
  STOCK_PRICE_ADJUSTMENT: "magenta",
};

export function CorrectionReportsPage() {
  const [page, setPage] = useState(0);
  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ["correctionOrders", "all", page],
    queryFn: () => sdk.GetCorrectionOrders({ page, limit }),
    select: (data) => data.getCorrectionOrders,
    keepPreviousData: true,
  });

  const [showExportModal, setShowExportModal] = useState(false);

  const { mutate: exportReport, isLoading: isExporting } = useMutation({
    mutationFn: async ({
      startDate,
      endDate,
    }: {
      startDate: string;
      endDate: string;
    }) => {
      const {
        exportCorrectionReport: { reportUrl },
      } = await sdk.ExportCorrectionReport({ startDate, endDate });
      window.open(reportUrl.startsWith("http") ? reportUrl : `/${reportUrl}`);
    },
    onSettled: () => setShowExportModal(false),
  });

  return (
    <main className="page">
      <DateRangeModal
        title="Залруулгын тайлан татах"
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={isExporting}
        onSubmit={(startDate, endDate) =>
          exportReport({
            startDate: startDate.format("YYYYMMDD"),
            endDate: endDate.format("YYYYMMDD"),
          })
        }
      />

      <div className="page-header">
        <h3>Залруулгын тайлан</h3>
        <Space size={12}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => setShowExportModal(true)}
          >
            Excel тайлан татах
          </Button>
        </Space>
      </div>
      <div className="page-body">
        <div className="page-table">
          <Table
            loading={isLoading}
            dataSource={data?.orders ?? []}
            rowKey="_id"
            size="small"
            pagination={{
              current: page + 1,
              pageSize: limit,
              total: (data?.totalPages ?? 1) * limit,
              onChange: (p) => setPage(p - 1),
              showSizeChanger: false,
            }}
            columns={[
              {
                title: "Огноо",
                dataIndex: "createdAt",
                width: 160,
                render: (v: number) =>
                  new Date(v).toISOString().replace("T", " ").slice(0, 19),
              },
              {
                title: "Хэрэглэгч",
                dataIndex: "user",
                width: 180,
                render: (user: any) =>
                  user
                    ? `${user.lastName ?? ""} ${user.firstName ?? ""}`.trim() ||
                      user.phoneNo
                    : "",
              },
              {
                title: "Утас",
                dataIndex: "user",
                key: "phone",
                width: 120,
                render: (user: any) => user?.phoneNo ?? "",
              },
              {
                title: "Төрөл",
                dataIndex: "correctionType",
                width: 140,
                render: (v: string) => (
                  <Tag color={CORRECTION_TYPE_COLORS[v] ?? "default"}>
                    {CORRECTION_TYPE_LABELS[v] ?? v}
                  </Tag>
                ),
              },
              {
                title: "Дүн",
                dataIndex: "amount",
                width: 140,
                render: (v: number, record: any) => {
                  const isStock =
                    record.correctionType === CorrectionType.StockAdjustment ||
                    record.correctionType === "STOCK_AMOUNT_ADJUSTMENT";
                  return (
                    <span>
                      {formatBalance({ balance: v })}
                      {isStock ? "" : " ₮"}
                    </span>
                  );
                },
              },
              {
                title: "Хуучин дүн",
                dataIndex: "oldValue",
                width: 140,
                render: (v: number | null, record: any) => {
                  if (v == null) return "—";
                  const isStock =
                    record.correctionType === CorrectionType.StockAdjustment ||
                    record.correctionType === "STOCK_AMOUNT_ADJUSTMENT";
                  return (
                    <span>
                      {isStock ? v : formatBalance({ balance: v })}
                      {isStock ? " ш" : " ₮"}
                    </span>
                  );
                },
              },
              {
                title: "Шинэ дүн",
                dataIndex: "newValue",
                width: 140,
                render: (v: number | null, record: any) => {
                  if (v == null) return "—";
                  const isStock =
                    record.correctionType === CorrectionType.StockAdjustment ||
                    record.correctionType === "STOCK_AMOUNT_ADJUSTMENT";
                  return (
                    <span>
                      {isStock ? v : formatBalance({ balance: v })}
                      {isStock ? " ш" : " ₮"}
                    </span>
                  );
                },
              },
              {
                title: "Тайлбар",
                dataIndex: "description",
                ellipsis: true,
              },
              {
                title: "Шалтгаан",
                dataIndex: "reason",
                ellipsis: true,
              },
            ]}
          />
        </div>
      </div>
    </main>
  );
}
